import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * Observe an element and report once it scrolls into view (kept `true` after), so a caller can defer
 * expensive work, such as a fetch or mounting a frame, until the element is visible. Where
 * `IntersectionObserver` is unavailable (e.g. jsdom in tests) it stays `false`.
 */
export function useInView<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}
